// ─────────────────────────────────────────────────────────────
// EconomicCalendarWidget
// Shows today's high-impact USD events + upcoming high+medium
// events for the rest of the week.
//
// Props:
//   todayEvents  — from useCalendar
//   eventsByDay  — from useCalendar
//   calLoading   — from useCalendar
//   dataTrustworthy — from useCalendar; gates any "no events" claim
//   calError     — from useCalendar; shown when data can't be trusted
//
// No internal fetch. Single source of truth is useCalendar.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";

const POST_FIRE_BUFFER_MS = 30 * 60 * 1000;
const CLOCK_TICK_MS       = 30 * 1000;

function etDayLabel(d) {
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    timeZone: "America/New_York",
  });
}

export function EconomicCalendarWidget({
  todayEvents = [],
  eventsByDay = {},
  calLoading = false,
  dataTrustworthy = false,
  calError = null,
}) {
  const [now, setNow] = useState(() => new Date());

  // Keep the clock live so "Fired" flips at release time and the 30-minute
  // post-fire window actually expires (previously set once on mount).
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const todayLabel = etDayLabel(now);

  const nowMs = now.getTime();

  // Today: high-impact only, show for 30 min after firing
  const visibleToday = todayEvents
    .filter(e => e.impact === "high" && e.dt.getTime() >= nowMs - POST_FIRE_BUFFER_MS)
    .slice(0, 6);

  // Upcoming: high + medium, future only, not today — flatten eventsByDay
  const upcomingEvents = Object.entries(eventsByDay)
    .filter(([label]) => label !== todayLabel)
    .flatMap(([, evs]) => evs)
    .filter(e =>
      (e.impact === "high" || e.impact === "medium") &&
      e.dt.getTime() > nowMs
    )
    .sort((a, b) => a.dt - b.dt)
    .slice(0, 8);

  const hasToday    = visibleToday.length > 0;
  const hasUpcoming = upcomingEvents.length > 0;

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle>
        Economic Calendar
        <span style={{ fontSize: 10, color: C.muted, fontWeight: 400, fontStyle: "italic" }}>USD events</span>
      </SectionTitle>

      {calLoading ? (
        <div style={{ color: C.muted, fontSize: 12 }}>Loading...</div>
      ) : (
        <>
          <div style={{
            fontSize: 10, fontWeight: 700, color: C.green,
            textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6,
          }}>
            Today — {todayLabel}
          </div>

          {hasToday ? (
            visibleToday.map((ev, i) => (
              <EventRow
                key={i}
                ev={ev}
                fired={ev.dt < now}
                isLast={i === visibleToday.length - 1 && !hasUpcoming}
              />
            ))
          ) : (
            <div style={{ fontSize: 11, color: dataTrustworthy ? C.muted : C.yellow, fontStyle: "italic", paddingBottom: 10 }}>
              {dataTrustworthy
                ? "Clean calendar — no high-impact events today."
                : "Calendar data can't be verified — check an external calendar."}
            </div>
          )}

          {hasUpcoming && (
            <>
              <div style={{
                fontSize: 10, fontWeight: 700, color: C.muted,
                textTransform: "uppercase", letterSpacing: "0.08em",
                borderTop: `1px solid ${C.border}`,
                paddingTop: 10, marginTop: hasToday ? 6 : 0, marginBottom: 6,
              }}>
                Upcoming
              </div>
              {upcomingEvents.map((ev, i) => (
                <UpcomingRow key={i} ev={ev} isLast={i === upcomingEvents.length - 1} />
              ))}
            </>
          )}

          {!dataTrustworthy && hasToday && (
            <div style={{ fontSize: 10, color: C.yellow, marginTop: 8, lineHeight: 1.4 }}>
              ⚠ {calError || "Calendar data may be stale."} Events shown may be incomplete.
            </div>
          )}

          {dataTrustworthy && !hasToday && !hasUpcoming && (
            <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>
              No events this week.
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Sub-components ─────────────────────────────────────────────

function EventRow({ ev, fired, isLast }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "70px 1fr 44px",
      gap: 8, alignItems: "center", padding: "7px 0",
      borderBottom: isLast ? "none" : `1px solid ${C.border}20`,
    }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, fontFamily: "monospace" }}>
        {ev.time}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: fired ? C.sub : C.text }}>
        {ev.event}
      </div>
      <div style={{ textAlign: "right", fontSize: 11, fontWeight: 700, color: fired ? C.muted : C.red }}>
        {fired ? "Fired" : "High"}
      </div>
    </div>
  );
}

function UpcomingRow({ ev, isLast }) {
  const isHigh     = ev.impact === "high";
  const badgeColor = isHigh ? C.red : C.yellow;
  const badgeLabel = isHigh ? "High" : "Med";

  // Day label from the event's dateLabel (e.g. "Fri")
  const dayShort = ev.dateLabel?.split(",")[0] || "";

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "32px 1fr 44px",
      gap: 8, alignItems: "center", padding: "6px 0",
      borderBottom: isLast ? "none" : `1px solid ${C.border}20`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>
        {dayShort}
      </div>
      <div style={{ fontSize: 11, color: C.sub }}>{ev.event}</div>
      <div style={{ textAlign: "right", fontSize: 11, fontWeight: 700, color: badgeColor }}>
        {badgeLabel}
      </div>
    </div>
  );
}
